import { ComponentProps, useCallback, useEffect, useState } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';
import IconButton from './button/IconButton';

/** Sets a value to `true` for a duration, then resets to `false`. */
function useDelayToggle(duration: number) {
  const [value, setValue] = useState(false);

  useEffect(() => {
    if (!value) return;

    const timer = setTimeout(() => {
      setValue(false);
    }, duration);

    /* oxlint-disable-next-line consistent-return */ // inconsistent return avoids creating a timer only to clear it immediately
    return () => clearTimeout(timer);
  }, [duration, value]);

  const trigger = useCallback(() => {
    setValue(true);
  }, []);

  return [value, trigger] as const;
}

type ClipboardButtonProps = {
  /** Returns the string to copy. Lazy so value can be resolved at click time. */
  onClick?: () => string;
  variant?: ComponentProps<typeof IconButton>['variant'];
  accessibilityLabel?: string;
  className?: string;
};

export default function ClipboardButton({
  onClick,
  variant,
  accessibilityLabel = 'Copy to clipboard',
  className,
}: ClipboardButtonProps) {
  const [, copyToClipboard] = useCopyToClipboard();
  const [copiedSuccess, triggerCopiedSuccess] = useDelayToggle(1000);

  const handleClipboard = useCallback(async () => {
    if (!onClick) return;
    await copyToClipboard(onClick());
    triggerCopiedSuccess();
  }, [copyToClipboard, onClick, triggerCopiedSuccess]);

  return (
    <IconButton
      icon={copiedSuccess ? 'ClipboardCheck' : 'Clipboard'}
      aria-label={accessibilityLabel}
      variant={variant}
      className={className}
      onClick={() => {
        handleClipboard().catch((error: unknown) => {
          // clipboard write can fail in restrictive iframe/permission contexts
          console.error(error);
        });
      }}
    />
  );
}
